import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    FiSearch,
    FiLayers,
    FiCode,
    FiTool,           // TestTube → FiTool (closest match)
    FiShield,         // ShieldCheck → FiShield
    FiBarChart2,      // BarChart3 → FiBarChart2 (very close)
    FiHeadphones,
    FiPlay,
    FiFlag,
    FiCheckCircle,
    FiArrowRight
} from "react-icons/fi";
import { FaPalette, FaRocket } from "react-icons/fa";
import './ProcessDrivenApproach.css';

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' },
    }),
};

const steps = [
    {
        title: 'Requirement Analysis',
        icon: FiSearch,
        items: ['Modelling', 'Use case documents', 'Swimlane Diagrams'],
        tools: ['Visio', 'Edrawmax', 'MS Office'],
        color: 'pda-color-1',
    },
    {
        title: 'Backlog Management',
        icon: FiLayers,
        items: ['Story Boarding', 'Defining User Stories', 'Managing Stories, Features and Sprints'],
        tools: ['Taiga', 'JIRA', 'Trello', 'Pivotal Tracker'],
        color: 'pda-color-2',
    },
    {
        title: 'UI/UX Design Process',
        icon: FaPalette,
        items: ['Research', 'User Journeys and Scenarios', 'Task Flows, Information Architecture', 'Wireframes & Mockups', 'Visual Designs'],
        tools: ['Balsamic', 'Invision', 'JusInMind', 'Photoshop', 'Sketch', 'Adobe XD'],
        color: 'pda-color-3',
    },
    {
        title: 'Development',
        icon: FiCode,
        items: ['Architecture', 'Low level design', 'Prototyping (Optional)', 'Build and CIT'],
        tools: ['Visual Studio', 'Eclipse', 'Netbeans'],
        color: 'pda-color-4',
    },
    {
        title: 'Agile Testing',
        icon: FiTool,
        items: ['Functional & Usability Testing (Manual and Automated)', 'In-Sprint automation testing', 'Simulated Testing', 'Load and Security Testing'],
        tools: ['Qualitia', 'Selenium'],
        color: 'pda-color-5',
    },
    {
        title: 'Review & Assurances',
        icon: FiShield,
        items: ['Code refactoring', 'Regression and Performance Testing', 'Sprint Demo & Retrospectives'],
        tools: ['ReSharper', 'JSRefactor', 'Sonar', 'Qualitia', 'JIRA'],
        color: 'pda-color-6',
    },
    {
        title: 'Release and Version Management',
        icon: FaRocket,
        items: ['Vulnerability tracking', 'Operational Monitoring'],
        tools: ['Jenkins', 'Maven', 'Bitbucket', 'SVN', 'GitHub'],
        color: 'pda-color-7',
    },
    {
        title: 'Governance & Performance Management',
        icon: FiBarChart2,
        items: ['Daily Standups', 'Weekly Status meetings', 'Burndown Charts', 'RAID logs', 'Steering Group'],
        tools: ['Taiga', 'JIRA', 'Trello', 'MS Project', 'MS Office'],
        color: 'pda-color-8',
    },
    {
        title: 'Product and BAU Support',
        icon: FiHeadphones,
        items: ['L2/L3 Support', 'Documentation', 'User Training', 'Releases'],
        tools: [],
        color: 'pda-color-9',
    },
];

const ProcessDrivenApproach = () => {
    return (
        <div className="pda-page">
            {/* Hero */}
            <motion.section
                className="pda-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
            >
                <motion.div
                    className="pda-hero-badge"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <FiPlay size={14} /> Our Methodology
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                    Offshore Development <br />
                    <span>Methodology</span> and <span>Tools</span>
                </motion.h1>

                <motion.p
                    className="pda-hero-subtitle"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45, duration: 0.5 }}
                >
                    Digital Engineering through value driven, customer focused, and design thinking approach
                </motion.p>

                {/* <motion.div
                    className="pda-breadcrumb"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    <Link to="/">Home</Link>
                    <span>/</span>
                    <span>Process Driven Approach</span>
                </motion.div> */}
            </motion.section>

            {/* Section Title */}
            <motion.div
                className="pda-section-title"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
            >
                <h2>
                    Offshore Development <span>Methodology and Tools</span>
                </h2>
                <div className="pda-title-underline" />
            </motion.div>

            {/* Timeline */}
            <div className="pda-timeline">
                <div className="pda-timeline-line" />

                {/* Start */}
                <motion.div
                    className="pda-timeline-pill"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                >
                    <span><FiPlay size={14} /> Start</span>
                </motion.div>

                {/* Steps */}
                {steps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                        <motion.div
                            key={index}
                            className={`pda-timeline-item ${step.color}`}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: '-40px' }}
                            variants={fadeUp}
                            custom={index}
                        >
                            <div className="pda-timeline-dot">
                                <FiCheckCircle />
                            </div>

                            <div className="pda-timeline-card">
                                <div className="pda-card-circle-tl" />
                                <div className="pda-card-circle-br" />
                                <span className="pda-step-number">{String(index + 1).padStart(2, '0')}</span>

                                <div className="pda-card-header">
                                    <div className="pda-card-icon">
                                        <Icon />
                                    </div>
                                    <h3>{step.title}</h3>
                                </div>

                                <ul className="pda-card-list">
                                    {step.items.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>

                                {step.tools.length > 0 && (
                                    <div className="pda-card-tools">
                                        <span className="pda-tools-label">Tools:</span>
                                        {step.tools.map((tool, i) => (
                                            <span key={i} className="pda-tool-chip">{tool}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    );
                })}

                {/* End */}
                <motion.div
                    className="pda-timeline-pill"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                >
                    <span><FiFlag size={14} /> End</span>
                </motion.div>
            </div>

            {/* CTA */}
            <motion.section
                className="pda-cta"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
            >
                <div className="pda-cta-card">
                    <h3>Ready to Start Your Project?</h3>
                    <p>Let us help you build world-class software with our proven methodology and expert team.</p>
                    <Link to="/contact">
                        <button className="pda-cta-btn">
                            Get Started <FiArrowRight size={18} />
                        </button>
                    </Link>
                </div>
            </motion.section>
        </div>
    );
};

export default ProcessDrivenApproach;
